import { supabase } from './supabase'
import { logAuditEvent } from './audit'

function handleError(error, fallback = 'Operation failed') {
  const msg = error?.message || fallback
  return { error: msg, data: null }
}

export const chatApi = {
  async listConversations(userId, role) {
    let query = supabase.from('conversations').select('*')
    if (role === 'admin') {
      // admins see all
    } else if (role === 'founder') {
      query = query.eq('founder_id', userId)
    } else {
      query = query.eq('agent_id', userId)
    }
    const { data, error } = await query.order('created_at', { ascending: false })
    if (error) return handleError(error)
    return { data: data || [], error: null }
  },

  async getConversation(conversationId) {
    const { data, error } = await supabase
      .from('conversations')
      .select('*')
      .eq('id', conversationId)
      .maybeSingle()
    if (error) return handleError(error)
    return { data, error: null }
  },

  async listMessages(conversationId) {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true })
    if (error) return handleError(error)
    return { data: data || [], error: null }
  },

  async sendMessage(conversationId, { senderType, senderEmail, body, attachmentUrl, attachmentName }) {
    const { data, error } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_type: senderType,
        sender_email: senderEmail,
        body,
        attachment_url: attachmentUrl || null,
        attachment_name: attachmentName || null,
      })
      .select()
      .single()
    if (error) return handleError(error)

    const { data: conv } = await supabase
      .from('conversations')
      .select('company_id, founder_id, agent_id')
      .eq('id', conversationId)
      .maybeSingle()

    if (conv?.company_id) {
      await logAuditEvent({
        company_id: conv.company_id,
        action: 'chat.message_sent',
        actor: senderEmail || senderType,
        details: body ? body.slice(0, 100) : `Attachment: ${attachmentName}`,
      })
    }

    const recipientId = senderType === 'founder' ? conv?.agent_id : conv?.founder_id
    if (recipientId) {
      await supabase.rpc('create_notification_with_email', {
        p_user_id: recipientId,
        p_type: 'chat_message',
        p_title: `New message from ${senderEmail || senderType}`,
        p_message: body ? body.slice(0, 200) : `Attachment: ${attachmentName}`,
        p_data: { conversation_id: conversationId },
      })
      // Fire email notification
      const { data: recipientProfile } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', recipientId)
        .maybeSingle()
      if (recipientProfile?.email) {
        try {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-notification-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: recipientProfile.email,
              type: 'chat_message',
              title: `New message from ${senderEmail || senderType}`,
              message: body ? body.slice(0, 200) : `Attachment: ${attachmentName}`,
              data: { conversation_id: conversationId },
            }),
          })
        } catch { /* email is best-effort */ }
      }
    }

    return { data, error: null }
  },

  async markRead(conversationId, userId) {
    const { error } = await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('conversation_id', conversationId)
      .is('read_at', null)
    return error ? handleError(error) : { error: null }
  },

  async createConversation({ companyId, founderId, agentRequestId, agentId, agentEmail }) {
    const { data: existing } = await supabase
      .from('conversations')
      .select('*')
      .eq('agent_request_id', agentRequestId)
      .maybeSingle()
    if (existing) return { data: existing, error: null }

    const { data, error } = await supabase
      .from('conversations')
      .insert({
        company_id: companyId,
        founder_id: founderId,
        agent_request_id: agentRequestId,
        agent_id: agentId,
        agent_email: agentEmail,
        status: 'active',
      })
      .select()
      .single()
    if (error) return handleError(error)
    return { data, error: null }
  },

  async canInitiateChat(userId, agentRequestId) {
    const { data: req } = await supabase
      .from('agent_requests')
      .select('status, agent_id, company_id')
      .eq('id', agentRequestId)
      .maybeSingle()
    if (!req) return { allowed: false, reason: 'Request not found' }
    if (req.status !== 'Assigned' && req.status !== 'Client Review' && req.status !== 'Finalized') {
      return { allowed: false, reason: `Agent has not accepted this request yet (status: ${req.status})` }
    }
    return { allowed: true, agentId: req.agent_id, companyId: req.company_id }
  },

  async getNotifications(userId) {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(20)
    if (error) return handleError(error)
    return { data: data || [], error: null }
  },

  async markNotificationRead(id) {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', id)
    return error ? handleError(error) : { error: null }
  },

  async markAllNotificationsRead(userId) {
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', userId)
      .eq('read', false)
    return error ? handleError(error) : { error: null }
  },

  async createAgentInquiry({ companyId, documentName, founderEmail, founderId, message }) {
    const { data, error } = await supabase
      .from('agent_requests')
      .insert({
        company_id: companyId,
        document_name: documentName,
        status: 'Open',
      })
      .select()
      .single()
    if (error) return handleError(error)

    // Notify all platform admins
    const { data: admins } = await supabase
      .from('profiles')
      .select('id, email')
      .eq('role', 'admin')

    if (admins && admins.length > 0) {
      await Promise.all(
        admins.map(async (admin) => {
          await supabase.rpc('create_notification_with_email', {
            p_user_id: admin.id,
            p_type: 'agent_request',
            p_title: `New agent request: ${documentName}`,
            p_message: message || `Agent inquiry for ${documentName} from ${founderEmail || 'a founder'}`,
            p_data: {
              agent_request_id: data.id,
              company_id: companyId,
              document_name: documentName,
              founder_email: founderEmail,
            },
          })
          // Fire email notification
          if (admin.email) {
            try {
              await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-notification-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  to: admin.email,
                  type: 'agent_request',
                  title: `New agent request: ${documentName}`,
                  message: message || `Agent inquiry for ${documentName} from ${founderEmail || 'a founder'}`,
                  data: { agent_request_id: data.id, company_id: companyId },
                }),
              })
            } catch { /* email is best-effort */ }
          }
        })
      )
    }

    return { data, error: null }
  },

  async subscribeToNotifications(userId, callback) {
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => callback(payload.new)
      )
      .subscribe()
    return () => supabase.removeChannel(channel)
  },
}
